import React, { useEffect, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Spacer from 'design-system/Spacer';
import Indicator from 'design-system/Indicator';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import ProgressBar from 'design-system/ProgressBar';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColors from 'hooks/useColors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { getPatternDayTradingInfo } from '../actions';
import DayTradeCards from '../components/DayTradeCards';
import { selectTotalDayTradeCount } from '../reducers/selectors';
import { isWeb } from '../../../constants';
// trying to reduce math computation
const getPosition = (totalDayTradeCount) => {
    if (totalDayTradeCount === 0) {
        return 0;
    }
    if (totalDayTradeCount === 1) {
        return 33;
    }
    if (totalDayTradeCount === 2) {
        return 66;
    }
    return 100;
};
const DayTradeCountScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const totalDayTradeCount = useAppSelector(selectTotalDayTradeCount);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    useEffect(() => {
        dispatch(getPatternDayTradingInfo());
    }, []);
    if (totalDayTradeCount === undefined) {
        return (<AppView type="secondary" withFrame={false}>
          <View testID="loadingContainer">
            <Indicator dark/>
          </View>
        </AppView>);
    }
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <CardView style={styles.contentContainer}>
            <View style={styles.infoContainer}>
              <View style={styles.headerContainer}>
                <Text Text-16>Day trades</Text>
                <Text Numbers-16>{totalDayTradeCount}/3</Text>
              </View>
              <Spacer h={16}/>
              <ProgressBar colors={colors.gradients.ultimate.colors} position={getPosition(totalDayTradeCount)}/>
              <Spacer h={16}/>
              <Text TextThin-14 Secondary>
                {'Pattern day trading is defined as a round-trip pair of trades within the same day on the same stock.\n\nFINRA, a trading governing body in the US, set a restriction on pattern day trading. An account is designated as a Pattern Day Trader if it makes 4 day trades within 5 business days.\n\nIf potential Pattern Day Trading activity is flagged on your account this may be restricted from further trading.\n\nExecuting 3 day trades or less within 5 business days will not designate the account as pattern day trader status.'}
              </Text>
              <Spacer h={16}/>
            </View>
            <DayTradeCards />
          </CardView>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Day trade count',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    headerContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    contentContainer: {
        paddingVertical: rem(16),
    },
    infoContainer: {
        paddingHorizontal: rem(16),
    },
});
export default DayTradeCountScreen;
